import type { PageContext } from './context.ts';
import { h, replace } from '../utils/dom.ts';
import { getLocale, t } from '../i18n/index.ts';
import { emptyState, loadInto, section, statusBadge, toast } from '../components/ui.ts';
import { getBookingRefs, importBookingRefFromHash } from '../services/device.ts';
import { errorMessageKey } from '../services/errors.ts';
import { canClientCancel } from '../utils/slots.ts';
import { formatDateTime, formatPrice, localized } from '../utils/format.ts';
import type { Booking } from '../utils/validation.ts';

export function myBookingsPage(ctx: PageContext): HTMLElement {
  const { business, repo } = ctx;
  const locale = getLocale();
  const list = h('div');
  const root = h('div', { class: 'page' }, h('h1', null, t('my.title')), list);

  importBookingRefFromHash(business.slug);
  const load = () => loadInto(list, () => repo.getBookings(getBookingRefs(business.slug)), render, undefined, errorMessageKey);

  function render(bookings: Booking[]): Node {
    if (bookings.length === 0) {
      return h('div', null,
        emptyState(t('my.empty')),
        h('a', { href: `/s/${business.slug}/`, class: 'btn btn-primary', 'data-link': true }, t('nav.book')),
      );
    }
    const now = Date.now();
    const sorted = [...bookings].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    const upcoming = sorted.filter((b) => Date.parse(b.ends_at) >= now);
    const past = sorted.filter((b) => Date.parse(b.ends_at) < now).reverse();
    return h('div', null,
      upcoming.length > 0 && section(t('my.upcoming'), h('ul', { class: 'booking-list' }, upcoming.map(item))),
      past.length > 0 && section(t('my.past'), h('ul', { class: 'booking-list' }, past.map(item))),
    );
  }

  function item(b: Booking): HTMLElement {
    const service = business.services.find((s) => s.id === b.service_id);
    const barber = business.barbers.find((x) => x.id === b.barber_id);
    const cancellable = canClientCancel(business, b, new Date());
    const isActive = b.status === 'pending' || b.status === 'confirmed';
    return h('li', { class: 'booking-item card' },
      h('div', { class: 'booking-item-main' },
        h('strong', null, formatDateTime(new Date(b.starts_at), locale, business.timezone)),
        h('span', null, `${service ? localized(service, 'name', locale) : b.service_id} · ${barber?.name ?? b.barber_id}`),
        h('span', { class: 'muted' }, formatPrice(b.price, locale, business.currency)),
      ),
      h('div', { class: 'booking-item-side' },
        statusBadge(b.status, t(`status.${b.status}`)),
        cancellable && h('button', { type: 'button', class: 'btn btn-ghost btn-sm', onClick: () => void cancel(b) }, t('my.cancel')),
        isActive && !cancellable && Date.parse(b.starts_at) > Date.now() &&
          h('small', { class: 'muted' }, t('my.tooLate', { hours: business.rules.cancellation_hours })),
      ),
    );
  }

  async function cancel(b: Booking): Promise<void> {
    if (!confirm(t('my.cancelConfirm'))) return;
    try {
      await repo.cancelByClient(b.id, b.cancel_token);
      toast(t('my.cancelled'), 'success');
    } catch (err) {
      toast(t(errorMessageKey(err)), 'error');
    }
    void load();
  }

  void load();
  // Background refresh: swap the list in place (no skeleton flash, buttons stay put).
  const refresh = () => repo.getBookings(getBookingRefs(business.slug)).then((b) => replace(list, render(b)), () => undefined);
  ctx.onDispose(repo.subscribe(() => void refresh()));
  return root;
}
